import { strFromU8, unzipSync } from 'fflate'

const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
const PDF_MIME_TYPE = 'application/pdf'

const hasExtension = (file, extension) => String(file && file.name || '').toLowerCase().endsWith(extension)

export const isDocxFile = (file) => Boolean(file) && (file.type === DOCX_MIME_TYPE || hasExtension(file, '.docx'))
export const isPdfFile = (file) => Boolean(file) && (file.type === PDF_MIME_TYPE || hasExtension(file, '.pdf'))
export const isImageFile = (file) => Boolean(file) && String(file.type || '').startsWith('image/')
export const isSupportedInvoiceFile = (file) => isImageFile(file) || isPdfFile(file) || isDocxFile(file)

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onload = () => resolve(reader.result)
  reader.onerror = () => reject(reader.error || new Error('Failed to read file.'))
  reader.readAsDataURL(file)
})

const loadPdfJs = () => {
  if (window.pdfjsLib) return Promise.resolve()

  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[data-gstocky-pdfjs]')
    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true })
      existingScript.addEventListener('error', reject, { once: true })
      return
    }

    const script = document.createElement('script')
    script.dataset.gstockyPdfjs = 'true'
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js'
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js'
      resolve()
    }
    script.onerror = () => reject(new Error('Failed to load the PDF reader.'))
    document.head.appendChild(script)
  })
}

const convertPdfToImage = async (file) => {
  await loadPdfJs()
  const pdf = await window.pdfjsLib.getDocument({ data: await file.arrayBuffer() }).promise
  const page = await pdf.getPage(1)
  const viewport = page.getViewport({ scale: 2 })
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  canvas.height = viewport.height
  canvas.width = viewport.width
  await page.render({ canvasContext: context, viewport }).promise
  return canvas.toDataURL('image/jpeg', 0.92)
}

const extractDocxText = async (file) => {
  let archive
  let documentIsTooLarge = false
  try {
    archive = unzipSync(new Uint8Array(await file.arrayBuffer()), {
      filter: (entry) => {
        if (entry.name !== 'word/document.xml') return false
        documentIsTooLarge = entry.originalSize > 5 * 1024 * 1024
        return !documentIsTooLarge
      }
    })
  } catch (error) {
    throw new Error('The DOCX file is invalid or damaged.')
  }

  if (documentIsTooLarge) throw new Error('The DOCX document contains too much text to scan.')
  const documentXml = archive['word/document.xml']
  if (!documentXml) throw new Error('The DOCX file does not contain a readable document.')

  const xml = new DOMParser().parseFromString(strFromU8(documentXml), 'application/xml')
  if (xml.querySelector('parsererror')) throw new Error('The DOCX document could not be read.')

  const paragraphs = Array.from(xml.getElementsByTagNameNS('*', 'p')).map((paragraph) => {
    let text = ''
    const walker = document.createTreeWalker(paragraph, NodeFilter.SHOW_ELEMENT)
    let node = walker.nextNode()
    while (node) {
      if (node.localName === 't') text += node.textContent || ''
      if (node.localName === 'tab') text += '\t'
      if (node.localName === 'br' || node.localName === 'cr') text += '\n'
      node = walker.nextNode()
    }
    return text.trim()
  }).filter(Boolean)

  const text = paragraphs.join('\n')
  if (!text) throw new Error('No readable text was found in the DOCX file.')
  return text
}

const wrapText = (context, text, maxWidth) => {
  const lines = []
  text.split(/\r?\n/).forEach((paragraph) => {
    const words = paragraph.replace(/\t/g, '    ').split(/\s+/).filter(Boolean)
    if (!words.length) {
      lines.push('')
      return
    }

    let line = ''
    words.forEach((word) => {
      const candidate = line ? `${line} ${word}` : word
      if (context.measureText(candidate).width <= maxWidth) {
        line = candidate
        return
      }

      if (line) lines.push(line)
      line = word
      while (context.measureText(line).width > maxWidth) {
        let splitAt = line.length - 1
        while (splitAt > 1 && context.measureText(line.slice(0, splitAt)).width > maxWidth) splitAt--
        lines.push(line.slice(0, splitAt))
        line = line.slice(splitAt)
      }
    })
    if (line) lines.push(line)
  })
  return lines
}

const renderTextToImage = (text) => {
  const width = 1600
  const padding = 80
  const lineHeight = 38
  const maxHeight = 12000
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  context.font = '26px Arial, sans-serif'

  const lines = wrapText(context, text, width - (padding * 2))
  const maxLines = Math.floor((maxHeight - (padding * 2)) / lineHeight)
  const visibleLines = lines.slice(0, maxLines)
  canvas.width = width
  canvas.height = Math.max(400, Math.min(maxHeight, (visibleLines.length * lineHeight) + (padding * 2)))

  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, canvas.width, canvas.height)
  context.fillStyle = '#111111'
  context.font = '26px Arial, sans-serif'
  context.textBaseline = 'top'
  visibleLines.forEach((line, index) => context.fillText(line, padding, padding + (index * lineHeight)))
  return canvas.toDataURL('image/jpeg', 0.92)
}

const convertDocxToImage = async (file) => renderTextToImage(await extractDocxText(file))

export const convertInvoiceFileToImage = async (file) => {
  if (!isSupportedInvoiceFile(file)) {
    throw new Error('Unsupported file type. Please upload a PDF, DOCX, PNG, or JPG file.')
  }
  if (isPdfFile(file)) return convertPdfToImage(file)
  if (isDocxFile(file)) return convertDocxToImage(file)
  return readAsDataUrl(file)
}
